import {
  InboxChangedDocument,
  type InboxChangedSubscription,
  type MessagingConversationScope,
} from '@gql'

import { useAutoRestartSubscription } from './useAutoRestartSubscription'

export type InboxChangedEvent = InboxChangedSubscription['inboxChanged']

/**
 * The inbox-list `inboxChanged` subscription, scoped to the current operator's
 * ASSIGNED inbox or the shared UNASSIGNED triage pool. Content-free: each event
 * names a conversation whose row entered/left/bumped, and the consumer patches
 * the row or refetches the list. Same transient-failure auto-restart as the
 * thread subscription (see useAutoRestartSubscription).
 *
 * `scope: null` skips the subscription entirely — for hosts with no inbox
 * list on screen (the case-page conversation drawer), where a list-change
 * feed would only burn a socket subscription + server fan-out for nothing.
 *
 * `operatorId` mirrors `conversationChanged` — the peer being viewed
 * (manager/team-lead cross-operator inbox, or the peer's read-only Recent
 * list). It must match the visible list's operator so the server's fan-out key
 * matches; it is ignored for the shared UNASSIGNED pool.
 */
export function useInboxChanged(
  scope: MessagingConversationScope | null | undefined,
  operatorId: string | undefined,
  onEvent: (event: InboxChangedEvent) => void,
): void {
  useAutoRestartSubscription(InboxChangedDocument, {
    label: 'inboxChanged',
    onEvent,
    select: (data) => data?.inboxChanged,
    skip: scope === null,
    variables: { operatorId, scope: scope ?? undefined },
  })
}
